import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { fetchServices } from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { Stagger, StaggerItem, Reveal } from "@/components/Reveal";
import { toFa } from "@/lib/fa";

const PageHero = () => (
  <section className="relative overflow-hidden pb-16 pt-36" data-testid="countries-page-hero">
    <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(212,175,55,0.1), transparent)" }} />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading as="h1" eyebrow="مقاصد مهاجرتی" title="کدام کشور <span class='gold-text'>نقطه شروع</span> شماست؟" desc="چهار حوزه قضایی تخصصی گروه ۷ با جزئیات کامل مسیرها و آمار واقعی هر کشور." />
    </div>
  </section>
);

export default function Countries() {
  const { data, isLoading } = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const services = data?.data ?? [];

  return (
    <>
      <PageHero />
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-96 animate-pulse rounded-3xl bg-navy-850/50" />)}</div>
        ) : (
          <Stagger className="grid gap-6 md:grid-cols-2">
            {services.map((s) => (
              <StaggerItem key={s.slug}>
                <Link to={`/countries/${s.slug}`} className="card-glow group flex h-full flex-col overflow-hidden rounded-3xl border border-gold/15 bg-navy-850" data-testid={`countries-page-card-${s.slug}`}>
                  <div className="relative h-60 overflow-hidden">
                    <img src={s.image} alt={s.name} className="img-treatment h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-850 via-navy-950/20 to-transparent" />
                    <span className="absolute right-5 top-5 rounded-full border border-gold/40 bg-navy-950/80 px-3 py-1 font-display text-[11px] font-black tracking-widest text-gold backdrop-blur" dir="ltr">{s.code}</span>
                    <div className="absolute bottom-4 right-5">
                      <h3 className="font-display text-2xl font-extrabold text-slate-50">{s.name}</h3>
                      <p className="mt-0.5 text-sm text-gold/90">{s.tagline}</p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="line-clamp-3 flex-1 text-sm leading-7 text-slate-400">{s.desc}</p>
                    <div className="mt-5 grid grid-cols-3 gap-3 rounded-2xl border border-gold/10 bg-navy-900/60 p-4 text-center">
                      {s.stats.map((st) => (
                        <div key={st.label}>
                          <p className="font-display text-sm font-black text-gold">{st.value}</p>
                          <p className="mt-1 text-[10px] text-slate-500">{st.label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 flex items-center justify-between border-t border-gold/10 pt-4">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-400" />{toFa(s.visaTypes.length)} مسیر فعال</span>
                        <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-gold" />نرخ موفقیت {s.successRate}</span>
                      </div>
                      <span className="flex items-center gap-2 text-sm font-bold text-gold transition-all group-hover:gap-3">
                        جزئیات مسیرها<ArrowLeft size={15} />
                      </span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        )}
        <Reveal className="mt-16">
          <div className="glass-panel flex flex-col items-center gap-4 rounded-3xl border border-gold/25 p-10 text-center">
            <h3 className="font-display text-xl font-extrabold sm:text-2xl">هنوز مطمئن نیستید کدام کشور برای شما بهتر است؟</h3>
            <p className="max-w-xl text-sm leading-7 text-slate-400">کارشناسان ما ۲۴ ساعته در خدمت شما هستند؛ بهترین مسیر را بر اساس پروفایل شما پیشنهاد می‌دهیم.</p>
            <Link to="/contact" className="btn-gold mt-2" data-testid="countries-consult-cta">درخواست مشاوره رایگان<ArrowLeft size={16} /></Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
